import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { useInvestments } from '../hooks/useInvestments';
import { useCalendar } from '../hooks/useCalendar';
import { InvestmentRecord, UpdateInvestmentRequest, BuyInvestmentRequest, SellInvestmentRequest } from '../services/api';
import InvestmentForm from '../components/InvestmentForm';
import TradingHeatmap from '../components/TradingHeatmap';
import { ChartIcon, EditIcon, DeleteIcon, FolderIcon, ListIcon, CloseIcon, ArrowUpTrendIcon, ArrowDownTrendIcon, DocumentIcon } from '../components/Icons';
import './InvestmentListPage.css';
import { getApiFailureMessage } from '../utils/apiErrorMessage';

// 종목별 요약 타입
interface StockSummary {
  stockName: string;
  stockCode?: string;
  assetType?: 'STOCK' | 'CURRENCY';
  currencyPair?: string;
  baseCurrency?: string;
  quoteCurrency?: string;
  avgExchangeRate?: number;
  totalBuyAmount: number;
  totalBuyQuantity: number;
  avgBuyPrice: number;
  totalSellQuantity: number;
  realizedProfit: number;
  holdingQuantity: number;
  records: InvestmentRecord[];
}

const ITEMS_PER_PAGE = 10;

const InvestmentListPage: React.FC = () => {
  const location = useLocation();
  const { investments, isLoading, error, fetchInvestments, createBuyInvestment, createSellInvestment, updateInvestment, deleteInvestment } = useInvestments();
  const { selectedDateRecords, fetchDateRecords } = useCalendar();
  
  // 뷰 모드: 'list' (전체 목록) | 'stock' (종목별)
  const [viewMode, setViewMode] = useState<'list' | 'stock'>('stock');
  const [filterType, setFilterType] = useState<'ALL' | 'BUY' | 'SELL'>('ALL');
  const [filterStockName, setFilterStockName] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'stockName' | 'amount'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // 페이징
  const [currentPage, setCurrentPage] = useState(1);
  const [currentStockPage, setCurrentStockPage] = useState(1); // 종목별 뷰용
  
  // 종목별 뷰에서 선택된 종목
  const [selectedStock, setSelectedStock] = useState<string | null>(null);
  
  // 수정/등록 모달
  const [editingRecord, setEditingRecord] = useState<InvestmentRecord | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    // location.state에서 날짜 받아오기 (DailyPortfolioModal에서 전달)
    if (location.state && (location.state as any).selectedDate) {
      return new Date((location.state as any).selectedDate);
    }
    return new Date();
  });

  // 초기 로드는 InvestmentsProvider가 담당한다.
  // filterType 변경은 investments 상태를 기준으로 UI에서 필터링되므로 별도 fetch가 필요 없다.

  // location.state에서 날짜를 받아서 자동으로 폼 열기
  useEffect(() => {
    if (location.state && (location.state as any).selectedDate) {
      const date = new Date((location.state as any).selectedDate);
      setSelectedDate(date);
      const dateStr = format(date, 'yyyy-MM-dd');
      fetchDateRecords(dateStr).then(() => {
        setShowForm(true);
        setEditingRecord(null); // 새 기록 작성 모드
      });
      // state 초기화 (다음 방문 시 다시 열리지 않도록)
      window.history.replaceState({}, document.title);
    }
  }, [location.state, fetchDateRecords]);

  // 페이지 변경 시 스크롤 상단으로
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // 종목코드로 통화 판단 (영문 대문자만 = 해외주식 = USD, 숫자만 = 국내주식 = KRW)
  const getCurrencyFromStockCode = (stockCode?: string) => {
    if (!stockCode) return 'KRW';
    if (/^[A-Z]+$/.test(stockCode)) return 'USD';
    if (/^\d+$/.test(stockCode)) return 'KRW';
    return 'KRW';
  };

  const formatCurrency = (amount?: number, currency: string = 'KRW') => {
    if (!amount && amount !== 0) return '-';
    const formatted = new Intl.NumberFormat('ko-KR', {
      minimumFractionDigits: currency === 'USD' ? 2 : 0,
      maximumFractionDigits: currency === 'USD' ? 2 : 0
    }).format(amount);
    return formatted + (currency === 'USD' ? ' USD' : '원');
  };

  const formatPercentage = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  // 종목별 요약 계산
  const stockSummaries = useMemo(() => {
    const summaryMap = new Map<string, StockSummary>();
    
    // 디버깅: 효성 종목 기록 확인
    const hyosungRecords = investments.filter(inv => 
      !inv.isDeleted && inv.stockName === '효성'
    );
    if (hyosungRecords.length > 0) {
      console.log('효성 종목 기록:', hyosungRecords.length, hyosungRecords.map(r => ({
        id: r.id,
        type: r.type,
        date: r.recordDate,
        buyReason: r.buyReason
      })));
    }
    
    investments
      .filter(inv => !inv.isDeleted)
      .forEach(record => {
        const key = record.stockName;
        
        if (!summaryMap.has(key)) {
          summaryMap.set(key, {
            stockName: record.stockName,
            stockCode: record.stockCode,
            assetType: record.assetType || 'STOCK',
            currencyPair: record.currencyPair,
            baseCurrency: record.baseCurrency,
            quoteCurrency: record.quoteCurrency,
            avgExchangeRate: 0,
            totalBuyAmount: 0,
            totalBuyQuantity: 0,
            avgBuyPrice: 0,
            totalSellQuantity: 0,
            realizedProfit: 0,
            holdingQuantity: 0,
            records: []
          });
        }
        
        const summary = summaryMap.get(key)!;
        summary.records.push(record);
        
        if (record.type === 'BUY') {
          // 외환거래일 때: 수량 × 환율 = 총 투자금액 (원화)
          if (record.assetType === 'CURRENCY' && record.quantity && record.exchangeRate) {
            const currencyTotalAmount = record.quantity * record.exchangeRate;
            summary.totalBuyAmount += currencyTotalAmount;
          } else {
            // 주식일 때: totalAmount가 있으면 사용, 없으면 pricePerShare × quantity 계산
            if (record.totalAmount) {
              summary.totalBuyAmount += record.totalAmount;
            } else if (record.pricePerShare && record.quantity) {
              summary.totalBuyAmount += record.pricePerShare * record.quantity;
            }
          }
          summary.totalBuyQuantity += record.quantity || 0;
          
          // 외환거래일 때 평균 환율 계산
          if (record.assetType === 'CURRENCY' && record.exchangeRate) {
            const buyRecords = summary.records.filter(r => r.type === 'BUY' && r.exchangeRate);
            if (buyRecords.length > 0) {
              const totalExchangeRate = buyRecords.reduce((sum, r) => sum + (r.exchangeRate || 0), 0);
              summary.avgExchangeRate = totalExchangeRate / buyRecords.length;
            }
          }
        } else if (record.type === 'SELL') {
          summary.totalSellQuantity += record.sellQuantity || 0;
          summary.realizedProfit += record.realizedProfitAmount || 0;
        }
      });
    
    // 평균 매수가 및 보유 수량 계산 (날짜순으로 매수/매도 기록 처리)
    summaryMap.forEach(summary => {
      // 날짜순으로 정렬된 모든 기록 (매수 + 매도)
      // 외환 거래는 exchangeRate, 주식은 pricePerShare 사용
      const allRecords = summary.records
        .filter(r => {
          if (r.type === 'BUY') {
            if (r.assetType === 'CURRENCY') {
              return r.quantity && r.exchangeRate;
            } else {
              // 수량만 있으면 포함 (0원 매수, 음수 금액 조정도 허용)
              return r.quantity !== undefined && r.quantity !== null;
            }
          } else if (r.type === 'SELL') {
            return r.sellQuantity;
          }
          return false;
        })
        .sort((a, b) => {
          const dateA = new Date(a.recordDate).getTime();
          const dateB = new Date(b.recordDate).getTime();
          if (dateA !== dateB) return dateA - dateB;
          // 같은 날짜면 기록된 순서대로 (ID 또는 생성 시간 순서)
          // 회사 분할의 경우 매도 후 매수가 맞지만, 계산 로직에서 처리하므로 여기서는 시간 순서 유지
          return (a.id || 0) - (b.id || 0);
        });
      
      // 날짜순으로 순회하며 보유 수량과 평단가를 동적으로 계산
      let totalBuyAmount = 0;
      let holdingQuantity = 0;
      let currentAvgPrice = 0;
      let currentAvgExchangeRate = 0; // 외환 거래용
      
      for (const record of allRecords) {
        if (record.type === 'BUY' && record.quantity !== undefined && record.quantity !== null) {
          const qty = record.quantity;
          let amount = 0;
          let price = 0;
          
          if (record.assetType === 'CURRENCY' && record.exchangeRate) {
            // 외환 거래: 수량 × 환율 = 총 투자금액 (원화)
            amount = qty * record.exchangeRate;
            price = record.exchangeRate; // 환율을 평균 환율로 사용
          } else if (record.pricePerShare !== undefined && record.pricePerShare !== null) {
            // 주식: 단가 × 수량 (0원도 허용)
            price = record.pricePerShare;
            amount = price * qty;
          } else if (record.totalAmount !== undefined && record.totalAmount !== null) {
            // totalAmount가 있으면 사용 (0원도 허용 - 액면분할 조정용)
            amount = record.totalAmount;
            price = qty !== 0 ? amount / qty : 0;
          } else {
            // pricePerShare와 totalAmount가 모두 없으면 0원으로 처리 (액면분할 조정 등)
            price = 0;
            amount = 0;
          }
          
          if (holdingQuantity === 0) {
            // 첫 매수
            totalBuyAmount = amount;
            holdingQuantity = qty;
            if (record.assetType === 'CURRENCY' && record.exchangeRate) {
              currentAvgExchangeRate = record.exchangeRate;
            } else {
              // 소수점 셋째자리에서 반올림
              currentAvgPrice = qty !== 0 ? Number((amount / qty).toFixed(3)) : 0;
            }
          } else {
            // 추가 매수: 평단가/평균 환율 재계산
            totalBuyAmount += amount;
            holdingQuantity += qty;
            if (holdingQuantity <= 0) {
              // 보유 수량이 0 이하가 되면 초기화
              holdingQuantity = 0;
              totalBuyAmount = 0;
              currentAvgPrice = 0;
              currentAvgExchangeRate = 0;
            } else {
              if (record.assetType === 'CURRENCY') {
                // 외환은 소수점 넷째자리에서 반올림 (환율 특성상)
                currentAvgExchangeRate = Number((totalBuyAmount / holdingQuantity).toFixed(4));
              } else {
                // 주식은 소수점 셋째자리에서 반올림
                currentAvgPrice = Number((totalBuyAmount / holdingQuantity).toFixed(3));
              }
            }
          }
        } else if (record.type === 'SELL' && record.sellQuantity) {
          // 매도: 보유 수량만 감소, 평단가/평균 환율은 유지
          const sellQty = record.sellQuantity;
          holdingQuantity -= sellQty;
          if (holdingQuantity < 0) holdingQuantity = 0;
          // 총 매수 금액도 비례하여 조정 (평단가/평균 환율 유지)
          if (holdingQuantity === 0) {
            totalBuyAmount = 0;
            currentAvgPrice = 0;
            currentAvgExchangeRate = 0;
          } else {
            if (summary.assetType === 'CURRENCY') {
              totalBuyAmount = currentAvgExchangeRate * holdingQuantity;
            } else {
              totalBuyAmount = currentAvgPrice * holdingQuantity;
            }
          }
        }
      }
      
      summary.holdingQuantity = holdingQuantity;
      summary.totalBuyAmount = totalBuyAmount;
      
      // 외환 거래는 평균 환율, 주식은 평균 매수가
      if (summary.assetType === 'CURRENCY') {
        summary.avgExchangeRate = currentAvgExchangeRate > 0 ? currentAvgExchangeRate : 0;
        summary.avgBuyPrice = 0; // 외환 거래는 avgBuyPrice 사용 안 함
      } else {
        summary.avgBuyPrice = currentAvgPrice > 0 ? currentAvgPrice : 0;
      }
      
      // 날짜 내림차순 정렬, 같은 날짜면 ID 순서대로 (기록된 순서대로)
      summary.records.sort((a, b) => {
        const dateA = new Date(a.recordDate).getTime();
        const dateB = new Date(b.recordDate).getTime();
        if (dateA !== dateB) return dateB - dateA; // 날짜 내림차순
        return (a.id || 0) - (b.id || 0); // 같은 날짜면 ID 오름차순 (기록된 순서)
      });
    });
    
    // 보유 수량 기준 정렬 (보유 중인 종목 우선)
    const summaries = Array.from(summaryMap.values())
      .sort((a, b) => b.holdingQuantity - a.holdingQuantity || b.totalBuyAmount - a.totalBuyAmount);
    
    // 디버깅: 효성 종목 요약 확인
    const hyosungSummary = summaries.find(s => s.stockName === '효성');
    if (hyosungSummary) {
      console.log('효성 종목 요약:', {
        stockName: hyosungSummary.stockName,
        recordsCount: hyosungSummary.records.length,
        records: hyosungSummary.records.map(r => ({
          id: r.id,
          type: r.type,
          date: r.recordDate,
          buyReason: r.buyReason
        }))
      });
    }
    
    return summaries;
  }, [investments]);

  // 매수 기록에서 종목 목록 추출 (매도 기록용)
  // 같은 종목은 하나로 그룹화하여 표시
  const getBuyStockList = useMemo(() => {
    const buyRecords = investments.filter(inv => inv.type === 'BUY' && !inv.isDeleted);
    const sellRecords = investments.filter(inv => inv.type === 'SELL' && !inv.isDeleted);
    
    // 종목별로 그룹화 (종목명 + 종목코드 조합을 키로 사용)
    const stockMap = new Map<string, { 
      name: string; 
      code?: string; 
      assetType?: string;
      currencyPair?: string;
      baseCurrency?: string;
      quoteCurrency?: string;
      firstRecordId: number; // 첫 번째 매수 기록 ID (매도 시 사용)
      buyQuantity: number;
      sellQuantity: number;
    }>();
    
    buyRecords.forEach(record => {
      if (record.quantity && record.quantity > 0) {
        // 종목별 키 생성 (종목명 + 종목코드 또는 통화페어)
        const key = record.assetType === 'CURRENCY' 
          ? `${record.currencyPair || record.stockName}`
          : `${record.stockName}_${record.stockCode || ''}`;
        
        if (!stockMap.has(key)) {
          // 첫 번째 매수 기록을 대표로 사용
          stockMap.set(key, {
            name: record.stockName,
            code: record.stockCode,
            assetType: record.assetType,
            currencyPair: record.currencyPair,
            baseCurrency: record.baseCurrency,
            quoteCurrency: record.quoteCurrency,
            firstRecordId: record.id,
            buyQuantity: 0,
            sellQuantity: 0
          });
        }
        
        const stock = stockMap.get(key)!;
        stock.buyQuantity += record.quantity;
      }
    });
    
    // 매도 기록으로 매도 수량 누적
    sellRecords.forEach(record => {
      if (record.selectedStockId && record.sellQuantity) {
        // 매도 기록의 selectedStockId로 해당 매수 기록 찾기
        const buyRecord = buyRecords.find(r => r.id === record.selectedStockId);
        if (buyRecord) {
          const key = buyRecord.assetType === 'CURRENCY' 
            ? `${buyRecord.currencyPair || buyRecord.stockName}`
            : `${buyRecord.stockName}_${buyRecord.stockCode || ''}`;
          
          const stock = stockMap.get(key);
          if (stock) {
            stock.sellQuantity += record.sellQuantity;
          }
        }
      }
    });
    
    // 보유 수량이 있는 종목만 필터링 (매수 수량 > 매도 수량)
    return Array.from(stockMap.values())
      .filter(stock => stock.buyQuantity > stock.sellQuantity)
      .map(stock => ({
        id: stock.firstRecordId, // 첫 번째 매수 기록 ID 사용
        label: stock.assetType === 'CURRENCY' 
          ? `${stock.currencyPair || stock.name} (외환)`
          : (stock.code ? `${stock.name} (${stock.code})` : stock.name),
        name: stock.name,
        code: stock.code,
        currencyPair: stock.currencyPair
      }));
  }, [investments]);

  // 필터링 및 정렬된 기록
  const filteredAndSortedInvestments = useMemo(() => {
    let filtered = [...investments].filter(inv => !inv.isDeleted);

    // 거래 유형 필터
    if (filterType !== 'ALL') {
      filtered = filtered.filter(inv => inv.type === filterType);
    }

    // 종목명 필터
    if (filterStockName) {
      filtered = filtered.filter(inv => 
        inv.stockName.toLowerCase().includes(filterStockName.toLowerCase()) ||
        inv.stockCode?.toLowerCase().includes(filterStockName.toLowerCase())
      );
    }

    // 선택된 종목 필터 (종목별 뷰에서)
    if (selectedStock) {
      filtered = filtered.filter(inv => inv.stockName === selectedStock);
    }

    // 정렬
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'date':
          comparison = new Date(a.recordDate).getTime() - new Date(b.recordDate).getTime();
          break;
        case 'stockName':
          comparison = (a.stockName || '').localeCompare(b.stockName || '');
          break;
        case 'amount':
          comparison = (a.totalAmount || 0) - (b.totalAmount || 0);
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [investments, filterType, filterStockName, sortBy, sortOrder, selectedStock]);

  // 페이징된 기록
  const paginatedInvestments = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredAndSortedInvestments.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredAndSortedInvestments, currentPage]);

  const totalPages = Math.ceil(filteredAndSortedInvestments.length / ITEMS_PER_PAGE);
  
  // 종목별 뷰 페이징
  const paginatedStockSummaries = useMemo(() => {
    if (viewMode !== 'stock' || selectedStock) return stockSummaries;
    const startIndex = (currentStockPage - 1) * ITEMS_PER_PAGE;
    return stockSummaries.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [stockSummaries, currentStockPage, viewMode, selectedStock]);
  
  const totalStockPages = Math.ceil(stockSummaries.length / ITEMS_PER_PAGE);

  // 기록 수정 핸들러
  const handleEdit = async (record: InvestmentRecord) => {
    const recordDate = new Date(record.recordDate);
    setSelectedDate(recordDate);
    setEditingRecord(record);
    
    const dateStr = format(recordDate, 'yyyy-MM-dd');
    await fetchDateRecords(dateStr);
    
    setShowForm(true);
  };

  // 기록 등록 처리 (신규 생성) - Promise<boolean> 반환
  const handleInvestmentSubmit = async (record: any): Promise<boolean> => {
    try {
      if (record.type === 'buy') {
        const buyRequest: BuyInvestmentRequest = {
          recordDate: record.date,
          assetType: record.assetType || 'STOCK',
          stockName: record.stockName,
          stockCode: record.stockCode || undefined,
          currencyPair: record.currencyPair || undefined,
          baseCurrency: record.baseCurrency || undefined,
          quoteCurrency: record.quoteCurrency || undefined,
          exchangeRate: record.exchangeRate ? parseFloat(record.exchangeRate.toString()) : undefined,
          investmentRatio: record.investmentRatio,
          quantity: record.quantity ? parseInt(record.quantity.toString()) : undefined,
          pricePerShare: record.buyPrice || undefined,
          dividendPerShare: record.dividend || undefined,
          buyReason: record.buyReason || undefined
        };

        const success = await createBuyInvestment(buyRequest);
        
        if (success) {
          await fetchInvestments();
          const dateStr = format(selectedDate, 'yyyy-MM-dd');
          await fetchDateRecords(dateStr);
          setEditingRecord(null);
          return true;
        } else {
          alert('매수 기록 생성에 실패했습니다.');
          return false;
        }
      } else if (record.type === 'sell') {
        if (!record.selectedStockId) {
          alert('매도할 종목을 선택해주세요.');
          return false;
        }

        const sellRequest: SellInvestmentRequest = {
          recordDate: record.date,
          selectedStockId: parseInt(record.selectedStockId),
          sellQuantity: record.sellQuantity ? parseInt(record.sellQuantity.toString()) : undefined,
          sellRatio: record.sellRatio ? parseFloat(record.sellRatio.toString()) : undefined,
          sellPrice: (record.sellPrice !== undefined && 
                      record.sellPrice !== null && 
                      record.sellPrice !== '') 
            ? parseFloat(record.sellPrice.toString()) 
            : undefined,
          sellReason: record.sellReason || undefined
        };

        const success = await createSellInvestment(sellRequest);
        
        if (success) {
          await fetchInvestments();
          const dateStr = format(selectedDate, 'yyyy-MM-dd');
          await fetchDateRecords(dateStr);
          setEditingRecord(null);
          return true;
        } else {
          alert('매도 기록 생성에 실패했습니다.');
          return false;
        }
      }
      return false;
    } catch (error: any) {
      console.error('투자 기록 생성 오류:', error);
      alert(`오류가 발생했습니다: ${getApiFailureMessage(error)}`);
      return false;
    }
  };

  // 기록 수정 처리 - Promise<boolean> 반환
  const handleInvestmentUpdate = async (recordId: number, record: any): Promise<boolean> => {
    try {
      let updateRequest: UpdateInvestmentRequest;
      
      if (record.type === 'buy') {
        updateRequest = {
          id: recordId,
          assetType: record.assetType || 'STOCK',
          stockName: record.stockName,
          stockCode: record.stockCode || undefined,
          currencyPair: record.currencyPair || undefined,
          baseCurrency: record.baseCurrency || undefined,
          quoteCurrency: record.quoteCurrency || undefined,
          exchangeRate: record.exchangeRate ? parseFloat(record.exchangeRate.toString()) : undefined,
          investmentRatio: record.investmentRatio,
          quantity: record.quantity ? parseInt(record.quantity.toString()) : undefined,
          pricePerShare: record.buyPrice ? parseFloat(record.buyPrice.toString()) : undefined,
          totalAmount: record.totalAmount ? parseFloat(record.totalAmount.toString()) : undefined,
          buyReason: record.buyReason !== undefined && record.buyReason !== null && record.buyReason !== '' 
            ? record.buyReason 
            : undefined
        };
      } else {
        updateRequest = {
          id: recordId,
          sellQuantity: record.sellQuantity ? parseInt(record.sellQuantity.toString()) : undefined,
          sellRatio: record.sellRatio ? parseFloat(record.sellRatio.toString()) : undefined,
          sellPrice: (record.sellPrice !== undefined && 
                      record.sellPrice !== null && 
                      record.sellPrice !== '') 
            ? parseFloat(record.sellPrice.toString()) 
            : undefined,
          realizedProfitRate: (record.realizedProfitRate !== undefined && 
                               record.realizedProfitRate !== null && 
                               record.realizedProfitRate !== '') 
            ? parseFloat(record.realizedProfitRate.toString()) 
            : undefined,
          sellReason: record.sellReason || undefined
        };
      }

      const success = await updateInvestment(updateRequest);
      
      if (success) {
        // 수정 후 데이터 갱신
        await fetchInvestments({
          type: filterType === 'ALL' ? undefined : filterType,
          page: 0,
          size: 1000
        });
        // 선택된 종목이 있으면 해당 종목의 기록도 갱신
        if (selectedStock) {
          const dateStr = format(selectedDate, 'yyyy-MM-dd');
          await fetchDateRecords(dateStr);
        }
        setEditingRecord(null);
        return true;
      } else {
        alert('투자 기록 수정에 실패했습니다.');
        return false;
      }
    } catch (error: any) {
      console.error('투자 기록 수정 오류:', error);
      alert(`오류가 발생했습니다: ${getApiFailureMessage(error)}`);
      return false;
    }
  };

  // 기록 삭제 처리
  const handleInvestmentDelete = async (recordId: number) => {
    try {
      const recordToDelete = investments.find(inv => inv.id === recordId);
      if (!recordToDelete) {
        alert('삭제할 기록을 찾을 수 없습니다.');
        return;
      }

      if (recordToDelete.type === 'BUY') {
        const relatedSellRecords = investments.filter(
          inv => inv.type === 'SELL' && inv.selectedStockId === recordId && !inv.isDeleted
        );
        
        if (relatedSellRecords.length > 0) {
          alert(
            `이 매수 기록과 연결된 매도 기록이 ${relatedSellRecords.length}개 있습니다.\n` +
            `매수 기록을 삭제하려면 먼저 연결된 매도 기록을 모두 삭제해주세요.`
          );
          return;
        }
      }

      const recordType = recordToDelete.type === 'BUY' ? '매수' : '매도';
      if (!window.confirm(`이 ${recordType} 기록을 삭제하시겠습니까?`)) {
        return;
      }

      const success = await deleteInvestment(recordId);
      
      if (success) {
        await fetchInvestments();
        alert('투자 기록이 성공적으로 삭제되었습니다.');
      } else {
        alert('투자 기록 삭제에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('투자 기록 삭제 오류:', error);
      alert(`오류가 발생했습니다: ${getApiFailureMessage(error)}`);
    }
  };

  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  
  const handleStockPageChange = (page: number) => {
    setCurrentStockPage(page);
  };

  // 종목 선택 핸들러
  const handleStockSelect = (stockName: string | null) => {
    setSelectedStock(stockName);
    setFilterStockName(''); // 종목 검색 필터 초기화 (중첩 방지)
    setCurrentPage(1);
    if (stockName) {
      setViewMode('list');
    }
  };

  // 렌더링 함수: 기록 카드
  const renderRecordCard = (record: InvestmentRecord) => (
    <div key={record.id} className={`investment-record-card ${record.type.toLowerCase()}`}>
      <div className="record-card-header">
        <div className="record-type-badge">
          <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}>
            {record.type === 'BUY' ? <ArrowUpTrendIcon size={14} color="currentColor" /> : <ArrowDownTrendIcon size={14} color="currentColor" />}
          </span>
          {record.type === 'BUY' ? '매수' : '매도'}
        </div>
        <div className="record-date">{formatDate(record.recordDate)}</div>
      </div>

      <div className="record-card-body">
        <div className="record-main-info">
          <h3>
            {record.assetType === 'CURRENCY' 
              ? (record.currencyPair || `${record.baseCurrency}/${record.quoteCurrency}`)
              : record.stockName}
          </h3>
          {record.assetType === 'CURRENCY' ? null : record.stockCode && (
            <span className="stock-code">{record.stockCode}</span>
          )}
        </div>

        {record.type === 'BUY' && (
          <div className="record-details">
            {record.quantity && (
              <div className="detail-item">
                <span className="detail-label">수량</span>
                <span className="detail-value">
                  {record.quantity.toLocaleString()}
                  {record.assetType === 'CURRENCY' 
                    ? ` ${record.baseCurrency || ''}` 
                    : '주'}
                </span>
              </div>
            )}
            {record.pricePerShare && (
              <div className="detail-item">
                <span className="detail-label">매수가</span>
                <span className="detail-value">{formatCurrency(record.pricePerShare, getCurrencyFromStockCode(record.stockCode))}</span>
              </div>
            )}
            {record.totalAmount && (
              <div className="detail-item">
                <span className="detail-label">투자금액</span>
                <span className="detail-value highlight">{formatCurrency(record.totalAmount, getCurrencyFromStockCode(record.stockCode))}</span>
              </div>
            )}
            {record.investmentRatio && (
              <div className="detail-item">
                <span className="detail-label">비율</span>
                <span className="detail-value">{record.investmentRatio.toFixed(2)}%</span>
              </div>
            )}
            {record.buyReason && (
              <div className="detail-item full-width">
                <span className="detail-label">매수 이유</span>
                <p className="detail-text">{record.buyReason}</p>
              </div>
            )}
          </div>
        )}

        {record.type === 'SELL' && (
          <div className="record-details">
            {record.sellQuantity && record.sellQuantity > 0 && (
              <div className="detail-item">
                <span className="detail-label">매도 수량</span>
                <span className="detail-value">
                  {record.sellQuantity.toLocaleString()}
                  {record.assetType === 'CURRENCY' 
                    ? ` ${record.baseCurrency || ''}` 
                    : '주'}
                </span>
              </div>
            )}
            {record.sellPrice != null && record.sellPrice > 0 && (
              <div className="detail-item">
                <span className="detail-label">매도 단가</span>
                <span className="detail-value">{formatCurrency(record.sellPrice, getCurrencyFromStockCode(record.stockCode))}</span>
              </div>
            )}
            {(record.realizedProfitRate !== undefined && 
              record.realizedProfitRate !== null) && (
              <div className="detail-item">
                <span className="detail-label">실현 손익률</span>
                <span className={`detail-value ${record.realizedProfitRate >= 0 ? 'profit' : 'loss'}`}>
                  {formatPercentage(record.realizedProfitRate)}
                </span>
              </div>
            )}
            {record.realizedProfitAmount !== undefined && 
             record.realizedProfitAmount !== null && (
              <div className="detail-item">
                <span className="detail-label">실현 손익</span>
                <span className={`detail-value highlight ${record.realizedProfitAmount >= 0 ? 'profit' : 'loss'}`}>
                  {formatCurrency(record.realizedProfitAmount, getCurrencyFromStockCode(record.stockCode))}
                </span>
              </div>
            )}
            {record.sellReason && (
              <div className="detail-item full-width">
                <span className="detail-label">매도 사유</span>
                <p className="detail-text">{record.sellReason}</p>
              </div>
            )}
          </div>
        )}

        <div className="record-actions">
          <button className="edit-btn" onClick={() => handleEdit(record)}>
            <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}><EditIcon size={16} color="#fff" /></span> 수정
          </button>
          <button className="delete-btn" onClick={() => handleInvestmentDelete(record.id)}>
            <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}><DeleteIcon size={16} color="#fff" /></span> 삭제
          </button>
        </div>
      </div>
    </div>
  );

  // 렌더링 함수: 페이지네이션
  const renderPagination = (page: number, totalPages: number, onPageChange: (page: number) => void) => {
    if (totalPages <= 1) return null;

    return (
      <div className="pagination">
        <button 
          className="page-btn page-nav-btn" 
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          aria-label="이전 페이지"
        >
          ‹
        </button>
        
        <div className="page-info">
          <select
            className="page-select"
            value={page}
            onChange={(e) => onPageChange(Number(e.target.value))}
            aria-label="페이지 선택"
          >
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <span className="page-total">/ {totalPages}</span>
        </div>
        
        <button 
          className="page-btn page-nav-btn" 
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
          aria-label="다음 페이지"
        >
          ›
        </button>
      </div>
    );
  };

  return (
    <div className="investment-list-page">
      <div className="page-header">
        <div>
          <h1><span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '8px' }}><ChartIcon size={24} color="#fff" /></span> 투자 일지</h1>
          <p>보유 종목과 투자 기록을 한눈에 확인하세요</p>
        </div>
        <button 
          className="new-record-btn"
          onClick={() => {
            setSelectedDate(new Date());
            const dateStr = format(new Date(), 'yyyy-MM-dd');
            fetchDateRecords(dateStr).then(() => {
              setShowForm(true);
              setEditingRecord(null); // 새 기록 작성 모드
            });
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}><EditIcon size={16} color="#fff" /></span> 새 기록 작성
        </button>
      </div>

      <TradingHeatmap investments={investments} />

      {/* 뷰 모드 선택 탭 */}
      <div className="view-mode-tabs">
        <button 
          className={`tab-btn ${viewMode === 'stock' && !selectedStock ? 'active' : ''}`}
          onClick={() => { setViewMode('stock'); setSelectedStock(null); setFilterStockName(''); setCurrentPage(1); setCurrentStockPage(1); }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}><FolderIcon size={16} color="currentColor" /></span> 종목별 보기
        </button>
        <button 
          className={`tab-btn ${viewMode === 'list' && !selectedStock ? 'active' : ''}`}
          onClick={() => { setViewMode('list'); setSelectedStock(null); setCurrentPage(1); setCurrentStockPage(1); }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}><ListIcon size={16} color="currentColor" /></span> 전체 목록
        </button>
        {selectedStock && (
          <div className="selected-stock-badge">
            <span>{selectedStock}</span>
            <button onClick={() => { setSelectedStock(null); setFilterStockName(''); setViewMode('stock'); setCurrentStockPage(1); }}><CloseIcon size={12} color="#fff" /></button>
          </div>
        )}
      </div>

      {/* 로딩 및 에러 */}
      {isLoading && (
        <div className="loading-message">투자 기록을 불러오는 중...</div>
      )}

      {error && (
        <div className="error-message">오류: {error}</div>
      )}

      {!isLoading && !error && (
        <>
          {/* 종목별 뷰 */}
          {viewMode === 'stock' && !selectedStock && (
            <div className="stock-view">
              <div className="stock-summary-header">
                <h2>보유 종목 ({stockSummaries.filter(s => s.holdingQuantity > 0).length}개)</h2>
                <p className="stock-summary-note">종목을 클릭하면 상세 기록을 확인할 수 있습니다</p>
              </div>
              
              <div className="stock-cards-grid">
                {paginatedStockSummaries.map(summary => (
                  <div 
                    key={summary.stockName}
                    className={`stock-summary-card ${summary.holdingQuantity > 0 ? 'holding' : 'sold'}`}
                    onClick={() => handleStockSelect(summary.stockName)}
                  >
                    <div className="stock-card-header">
                      <h3>
                        {summary.assetType === 'CURRENCY' 
                          ? (summary.currencyPair || `${summary.baseCurrency}/${summary.quoteCurrency}` || summary.stockName)
                          : summary.stockName}
                      </h3>
                      {summary.assetType === 'STOCK' && summary.stockCode && (
                        <span className="stock-code">{summary.stockCode}</span>
                      )}
                      {summary.assetType === 'CURRENCY' && (
                        <span className="stock-code" style={{ background: '#e3f2fd', color: '#1976d2' }}>
                          💱 외환
                        </span>
                      )}
                      <span className={`holding-badge ${summary.holdingQuantity > 0 ? 'active' : ''}`}>
                        {summary.holdingQuantity > 0 ? '보유중' : '전량매도'}
                      </span>
                    </div>
                    
                    <div className="stock-card-stats">
                      <div className="stat-item">
                        <span className="stat-label">보유 수량</span>
                        <span className="stat-value">
                          {summary.assetType === 'CURRENCY' 
                            ? `${summary.holdingQuantity.toLocaleString()} ${summary.baseCurrency || ''}`
                            : `${summary.holdingQuantity.toLocaleString()}주`}
                        </span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">총 투자금액</span>
                        <span className="stat-value">
                          {summary.assetType === 'CURRENCY' 
                            ? formatCurrency(summary.totalBuyAmount, 'KRW')
                            : formatCurrency(summary.totalBuyAmount, getCurrencyFromStockCode(summary.stockCode))}
                        </span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">
                          {summary.assetType === 'CURRENCY' ? '평균 환율' : '평균 매수가'}
                        </span>
                        <span className="stat-value">
                          {summary.assetType === 'CURRENCY' 
                            ? (summary.avgExchangeRate 
                                ? summary.avgExchangeRate.toLocaleString('ko-KR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })
                                : '-')
                            : formatCurrency(Number(summary.avgBuyPrice.toFixed(2)), getCurrencyFromStockCode(summary.stockCode))}
                        </span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">실현 손익</span>
                        <span className={`stat-value ${summary.realizedProfit >= 0 ? 'profit' : 'loss'}`}>
                          {summary.assetType === 'CURRENCY' 
                            ? formatCurrency(summary.realizedProfit, 'KRW')
                            : formatCurrency(summary.realizedProfit, getCurrencyFromStockCode(summary.stockCode))}
                        </span>
                      </div>
                    </div>
                    
                    <div className="stock-card-footer">
                      <span className="record-count"><span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}><DocumentIcon size={14} color="currentColor" /></span> {summary.records.length}개의 기록</span>
                      <span className="view-detail">상세 보기 →</span>
                    </div>
                  </div>
                ))}
              </div>
              
              {stockSummaries.length === 0 && (
                <div className="empty-state">
                    <p><span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}><DocumentIcon size={16} color="currentColor" /></span> 등록된 종목이 없습니다.</p>
                  <p>달력에서 날짜를 선택하여 투자 기록을 작성해보세요.</p>
                </div>
              )}
              
              {/* 종목별 뷰 페이지네이션 */}
              {renderPagination(currentStockPage, totalStockPages, handleStockPageChange)}
            </div>
          )}

          {/* 목록 뷰 또는 선택된 종목의 기록 */}
          {(viewMode === 'list' || selectedStock) && (
            <>
              {/* 필터 섹션 */}
              <div className="filters-section">
                <div className="filter-group">
                  <label>거래 유형</label>
                  <select 
                    value={filterType} 
                    onChange={(e) => { setFilterType(e.target.value as 'ALL' | 'BUY' | 'SELL'); setCurrentPage(1); }}
                  >
                    <option value="ALL">전체</option>
                    <option value="BUY">매수</option>
                    <option value="SELL">매도</option>
                  </select>
                </div>

                {!selectedStock && (
                  <div className="filter-group">
                    <label>종목 검색</label>
                    <input
                      type="text"
                      placeholder="종목명 또는 종목코드"
                      value={filterStockName}
                      onChange={(e) => { setFilterStockName(e.target.value); setCurrentPage(1); }}
                    />
                  </div>
                )}

                <div className="filter-group">
                  <label>정렬</label>
                  <select 
                    value={sortBy} 
                    onChange={(e) => setSortBy(e.target.value as 'date' | 'stockName' | 'amount')}
                  >
                    <option value="date">날짜</option>
                    <option value="stockName">종목명</option>
                    <option value="amount">투자금액</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>순서</label>
                  <select 
                    value={sortOrder} 
                    onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                  >
                    <option value="desc">최신순</option>
                    <option value="asc">오래된순</option>
                  </select>
                </div>
              </div>

              {/* 기록 요약 */}
              <div className="records-summary">
                <span>총 <strong>{filteredAndSortedInvestments.length}</strong>개의 기록</span>
                {totalPages > 1 && (
                  <span className="page-info">
                    {currentPage} / {totalPages} 페이지
                  </span>
                )}
              </div>

              {/* 기록 목록 */}
              <div className="investment-records-list">
                {paginatedInvestments.length === 0 ? (
                  <div className="empty-state">
                    <p><span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}><DocumentIcon size={16} color="currentColor" /></span> 투자 기록이 없습니다.</p>
                    <p>달력에서 날짜를 선택하여 투자 기록을 작성해보세요.</p>
                  </div>
                ) : (
                  paginatedInvestments.map(record => renderRecordCard(record))
                )}
              </div>

              {/* 페이지네이션 */}
              {renderPagination(currentPage, totalPages, handlePageChange)}
            </>
          )}
        </>
      )}

      {/* 등록/수정 모달 */}
      {showForm && (
        <InvestmentForm
          key={`form-${editingRecord ? editingRecord.id : 'new'}-${format(selectedDate, 'yyyy-MM-dd')}`}
          onSubmit={handleInvestmentSubmit}
          onUpdate={handleInvestmentUpdate}
          onDelete={handleInvestmentDelete}
          onCancel={() => {
            setShowForm(false);
            setEditingRecord(null);
          }}
          selectedDate={selectedDate}
          existingRecords={selectedDateRecords.length > 0 ? selectedDateRecords : (editingRecord ? [editingRecord] : [])}
          allInvestments={investments}
          existingStocks={getBuyStockList}
          initialEditingRecordId={editingRecord?.id}
        />
      )}
    </div>
  );
};

export default InvestmentListPage;
